import DataRow from '@/libs/antd/components/DataRow';
import FleetModal from '@/libs/antd/components/Modal';
import FleetTitle from '@/libs/antd/components/Title';
import { getSurveyDataInformation } from '@/libs/fleet-api/services/survey';
import { FileSearchOutlined, LoadingOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Divider, Table } from 'antd';
import React, { Fragment, useState } from 'react';
import { useModals } from './ModalProvider';
import { FleetFile } from '@/libs/fleet-api/fetcher';
import dayjs from 'dayjs';
import { formatCurrency } from '@/libs/antd/formater';

export type PraSurveyDataModalState = null | {
	prospectId: string;
};

type PraSurveyDataModalProps = {
	modal: PraSurveyDataModalState;
	onClose: () => void;
};

const PraSurveyDataModal: React.FC<PraSurveyDataModalProps> = ({
	onClose,
	modal,
}) => {
	const { data, isLoading } = useQuery({
		queryKey: ['getSurveyDataInformation', modal?.prospectId] as const,
		queryFn: ({ queryKey }) => {
			const [, id] = queryKey;
			if (!id) return null;
			return getSurveyDataInformation(id);
		},
		enabled: !!modal?.prospectId,
	});

	const {
		mainInformation,
		customerLegalDocuments,
		customerBusinessDocuments,
		customerReferralDocuments,
		employmentContracts,
		prospectBankStatements,
	} = data?.data ?? {};

	const {
		openPreviewModal,
		openEmploymentContractModal,
		openBankStatementModal,
	} = useModals();

	if (isLoading) {
		return (
			<LoadingOutlined
				style={{
					marginTop: 160,
					fontSize: 100,
					color: '#1557BC',
					display: 'block',
				}}
			/>
		);
	}
	return (
		<FleetModal
			open={Boolean(modal)}
			width={940}
			cancelText="BACK"
			buttonPosition="end"
			closable={false}
			okButtonProps={{
				hidden: true,
			}}
			onCancel={() => onClose?.()}
		>
			<Fragment>
				<FleetTitle>Main Information</FleetTitle>
				<DataRow
					loading={isLoading}
					items={[
						[
							['Nama Customer', mainInformation?.customerName],
							['Prospect ID', mainInformation?.prospectId],
						],
						[
							['Kelas Industri', mainInformation?.industrySector.industryClass],
							['Sumber Order', mainInformation?.orderSource],
						],
						[
							[
								'Portofolio Guideline',
								mainInformation?.industrySector.portofolioGuideline,
							],
							[
								'SLIK Result',
								mainInformation?.slikResult
									? 'Kol ' + mainInformation?.slikResult
									: '-',
							],
						],
						[
							['FRM Name', mainInformation?.frm.value],
							['CA Name', mainInformation?.creditAnalyst.value],
						],
					]}
					style={{ marginBottom: 20 }}
				/>

				<Divider />

				<FleetTitle>Data Legalitas</FleetTitle>
				<Table
					scroll={{ x: 'max-content' }}
					dataSource={
						customerLegalDocuments?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											if (!record.file?.id) return;
											openPreviewModal(record.file as FleetFile);
										}}
										style={!record.file?.id ? { color: '#bababa' } : {}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Jenis Dokumen',
							dataIndex: ['documentType'],
						},
						{
							title: 'No Dokumen',
							dataIndex: ['documentNo'],
						},
						{
							title: 'Tanggal Dokumen',
							dataIndex: ['documentDate'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				<FleetTitle>Data Perizinan Usaha</FleetTitle>

				<Table
					dataSource={
						customerBusinessDocuments?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											if (!record.file?.id) return;

											openPreviewModal({
												id: record.file.id as unknown as number,
												name: record.file.name,
												url: record.file.url,
											});
										}}
										style={!record.file?.id ? { color: '#bababa' } : {}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Jenis Dokumen',
							dataIndex: ['documentType'],
						},
						{
							title: 'No Dokumen',
							dataIndex: ['documentNo'],
						},
						{
							title: 'Penerbit',
							dataIndex: ['publisher', 'value'],
						},
						{
							title: 'Tanggal Dokumen',
							dataIndex: ['documentDate'],
						},
						{
							title: 'Masa Berlaku',
							dataIndex: ['validityPeriod', 'value'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />

				{customerReferralDocuments && customerReferralDocuments.length > 0 && (
					<>
						<FleetTitle>Data Referral JF</FleetTitle>
						<Table
							scroll={{ x: 'max-content' }}
							dataSource={
								customerReferralDocuments?.map((record, key) => {
									return { ...record, key };
								}) ?? []
							}
							columns={[
								{
									title: 'View Detail',
									align: 'center',
									render: (_, record) => {
										return (
											<a
												href="#"
												onClick={(e) => {
													e.preventDefault();
													if (!record.file?.id) return;
													openPreviewModal(record.file as FleetFile);
												}}
												style={!record.file?.id ? { color: '#bababa' } : {}}
											>
												<FileSearchOutlined />
											</a>
										);
									},
								},
								{
									title: 'Jenis Dokumen',
									dataIndex: ['documentType'],
								},
								{
									title: 'No Dokumen',
									dataIndex: ['documentNo'],
								},
								{
									title: 'Tanggal Dokumen',
									dataIndex: ['documentDate'],
								},
							]}
							bordered
							pagination={false}
							style={{ marginBottom: 20 }}
						/>
						<Divider />
					</>
				)}

				<FleetTitle>Data Kontrak Kerja</FleetTitle>
				<Table
					dataSource={
						employmentContracts?.map((record, key) => {
							return {
								...record,
								paymentMethodValue: record.paymentMethod,
								key,
							};
						}) ?? []
					}
					columns={[
						{
							title: 'View Kontrak Kerja',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openEmploymentContractModal(record);
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Bouwheer',
							dataIndex: ['bouwheer', 'name'],
						},
						{
							title: 'Bouwheer ID',
							dataIndex: ['bouwheer', 'id'],
						},
						{
							title: 'Kelas Industri',
							render: () => '-',
						},
						{
							title: 'Nama Pekerjaan',
							dataIndex: ['jobTitle'],
						},
						{
							title: 'No. SPK',
							dataIndex: ['spkNumber'],
						},
						{
							title: 'Lokasi Pekerjaan',
							dataIndex: ['jobLocation'],
						},
						{
							title: 'Detail Kontrak',
							dataIndex: ['contractDetail'],
						},
						{
							title: 'Nilai Kontrak',
							dataIndex: ['contractValue'],
							render: (_, record) => formatCurrency(record.contractValue),
						},
						{
							title: 'Tanggal Mulai',
							render: (_, record) =>
								dayjs(record.startDate).format('DD/MM/YYYY'),
						},
						{
							title: 'Tanggal Berakhir',
							render: (_, record) => dayjs(record.endDate).format('DD/MM/YYYY'),
						},
						{
							title: 'Tenor',
							dataIndex: ['tenor'],
							render: (_, record) => record.tenor ?? '-',
						},
						{
							title: 'Sistem Pembayaran',
							dataIndex: ['paymentMethodValue', 'value'],
							render: (_, record) => record.paymentMethodValue?.value ?? '-',
						},
						{
							title: 'TOP Pembayaran',
							dataIndex: ['termOfPayment'],
							render: (_, record) => record.termOfPayment ?? '-',
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20, maxWidth: '100%', overflowX: 'auto' }}
				/>
				<Divider />

				<FleetTitle>Data Rekening Bank</FleetTitle>
				<Table
					dataSource={
						prospectBankStatements?.map((record, key) => {
							return { ...record, key };
						}) ?? []
					}
					columns={[
						{
							title: 'View Detail',
							align: 'center',
							render: (_, record) => {
								return (
									<a
										href="#"
										onClick={(e) => {
											e.preventDefault();
											openBankStatementModal(record);
										}}
									>
										<FileSearchOutlined />
									</a>
								);
							},
						},
						{
							title: 'Jenis Rekening	',
							dataIndex: ['accountType', 'value'],
						},
						{
							title: 'Nama Bank',
							dataIndex: ['bankName', 'value'],
						},
						{
							title: 'Account Number',
							dataIndex: ['accountNo'],
						},
						{
							title: 'Periode',
							render: (_, record) =>
								dayjs(record.bankStatementPeriod).format('MM/YYYY'),
						},
						{
							title: 'Total Debet',
							dataIndex: ['totalDebit'],
						},
						{
							title: 'Total Kredit',
							dataIndex: ['totalCredit'],
						},
						{
							title: 'Average Saldo',
							dataIndex: ['averageBalance'],
						},
					]}
					bordered
					pagination={false}
					style={{ marginBottom: 20 }}
				/>
				<Divider />
			</Fragment>
		</FleetModal>
	);
};

export default PraSurveyDataModal;

export const usePraSurveyDataModal = () => {
	return useState<PraSurveyDataModalState>(null);
};
